/**
 * SEO component — per-page <title>, meta tags, canonical en JSON-LD.
 *
 * Geen Helmet-dependency: React 19 + react-helmet-async heeft compatibiliteits-
 * problemen bij dynamische titles. Directe DOM-manipulatie via useEffect werkt
 * stabiel, is super-licht en blijft volledig SEO-effectief (Google rendert JS).
 */
import { useEffect } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_IMAGE = "https://omasverhaaltjes.nl/og-cover.png";
const SITE_URL = "https://omasverhaaltjes.nl";
const DEFAULT_TITLE =
  "Oma's Verhaaltjes — Magische slaapverhaaltjes voor kinderen 3-8 jaar";
const DEFAULT_DESC =
  "Magische slaapverhaaltjes voor kinderen van 3-8 jaar, voorgelezen met een warme oma-stem.";

const SEO_TAG_FLAG = "data-seo-managed";
const SEO_JSONLD_ID = "seo-dynamic-jsonld";

function setMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    for (const [k, v] of Object.entries(attrs)) {
      if (k === "content") continue;
      el.setAttribute(k, v);
    }
    el.setAttribute(SEO_TAG_FLAG, "1");
    document.head.appendChild(el);
  }
  if (attrs.content !== undefined) el.setAttribute("content", attrs.content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(SEO_TAG_FLAG, "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(data) {
  // Verwijder oude dynamische JSON-LD (laat de statische uit index.html staan).
  const old = document.getElementById(SEO_JSONLD_ID);
  if (old) old.remove();
  if (!data) return;
  const s = document.createElement("script");
  s.id = SEO_JSONLD_ID;
  s.type = "application/ld+json";
  s.text = JSON.stringify(data);
  document.head.appendChild(s);
}

const SEO = ({
  title,
  description,
  image,
  url,
  path,
  type = "website",
  jsonLd,
  noindex = false,
}) => {
  useEffect(() => {
    let cancelled = false;

    const apply = (remote) => {
      if (cancelled) return;
      const t = String(title || remote?.title || DEFAULT_TITLE);
      const d = String(description || remote?.description || DEFAULT_DESC);
      const img = String(image || remote?.image || DEFAULT_IMAGE);
      const u = String(
        url || remote?.url ||
        (path ? `${SITE_URL}${path}` : window.location.href)
      );
      const ogType = String(remote?.og_type || type);

      document.title = t;
      setMeta('meta[name="description"]', { name: "description", content: d });
      setMeta('meta[name="robots"]', {
        name: "robots",
        content: noindex
          ? "noindex, nofollow"
          : "index, follow, max-snippet:-1, max-image-preview:large",
      });
      setLink("canonical", u);
      setMeta('meta[property="og:type"]', { property: "og:type", content: ogType });
      setMeta('meta[property="og:url"]', { property: "og:url", content: u });
      setMeta('meta[property="og:title"]', { property: "og:title", content: t });
      setMeta('meta[property="og:description"]', { property: "og:description", content: d });
      setMeta('meta[property="og:image"]', { property: "og:image", content: img });
      setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
      setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: t });
      setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: d });
      setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: img });

      setJsonLd(jsonLd || remote?.jsonld);
    };

    // Eerst lokale props toepassen — daarna remote info fetchen indien path
    apply(null);

    if (path && !title) {
      fetch(`${API}/api/seo/meta?path=${encodeURIComponent(path)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => { if (data) apply(data); })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
      // JSON-LD opruimen op unmount, andere meta blijven (Helmet-stijl)
      setJsonLd(null);
    };
  }, [title, description, image, url, path, type, jsonLd, noindex]);

  return null;
};

export default SEO;
